import { Component, OnInit, Signal, WritableSignal, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Culture, LocaleService, cultures } from '@cloud-and-service/portal-lib';
import { TranslationService } from 'src/app/services/translation.service';
import { catchError, delay, finalize, from, of, retry, tap } from 'rxjs';

@Component({
    selector: 'app-language-selector',
    templateUrl: './language-selector.component.html',
    styleUrls: ['./language-selector.component.scss'],
    standalone: true,
    imports: [FormsModule],
})
export class LanguageSelectorComponent implements OnInit {
    availableCultures: Signal<Culture[]>;
    selectedLanguage: Signal<string | null>;
    isLoading: WritableSignal<boolean> = signal(false);
    private availableIsoCodes: WritableSignal<string[]> = signal([]);

    constructor(
        private localeService: LocaleService,
        private translationService: TranslationService
    ) {
        this.selectedLanguage = this.localeService.currentLanguageSignal;

        this.availableCultures = computed(() => {
            const isoCodes = this.availableIsoCodes();

            if (isoCodes.length === 0) {
                return cultures;
            }

            const normalizedCodes = isoCodes.map(code => code.toLowerCase());
            const filtered = cultures.filter(c =>
                normalizedCodes.includes(c.isoCode.toLowerCase())
            );

            if (!filtered.find(c => c.language === 'da')) {
                const danish = cultures.find(c => c.language === 'da');
                if (danish) {
                    filtered.unshift(danish);
                }
            }

            return filtered;
        });
    }

    ngOnInit(): void {
        this.loadAvailableLanguages();
    }

    changeLocale(event: Event): void {
        const target = event.target as HTMLSelectElement;
        this.localeService.updateLocale(target.value);
    }

    private loadAvailableLanguages(): void {
        this.isLoading.set(true);

        from(this.translationService.getAvailableLanguages())
            .pipe(
                retry({
                    count: 3,
                    delay: (error, retryCount) => {
                        const delayMs = 1000 * Math.pow(2, retryCount - 1); // 1s, 2s, 4s
                        console.warn(`Failed to fetch available languages (attempt ${retryCount}/4). Retrying in ${delayMs}ms...`);
                        return of(error).pipe(delay(delayMs));
                    }
                }),
                tap(isoCodes => this.availableIsoCodes.set(isoCodes)),
                catchError(err => {
                    console.error('Failed to fetch available languages after all retry attempts:', err);
                    // Return empty array to show all cultures as fallback
                    return of([]);
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }
}
